import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LayoutDashboard, 
  TrendingUp, 
  History, 
  Bell, 
  LogOut,
  Menu,
  X,
  Shield,
  CreditCard,
  ChevronRight,
  Settings,
  Play,
  Mail,
  MessageCircle,
  HelpCircle,
  Sun,
  Moon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import AIChatbot from '@/components/AIChatbot';
import SettingsPanel from '@/components/SettingsPanel';
import { useTheme } from '@/contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { API, getAuthHeaders } from '../utils/api';

// TradeForge Logo Component
const TradeForgelogo = ({ className = "w-8 h-8" }) => (
  <img 
    src="https://static.prod-images.emergentagent.com/jobs/df6b9931-f35a-4f03-9adf-4a4572c904c3/images/6dfa2d55642f0060455034e074f9f811d7710ca6e7452f73c1fd5e309a79f2ae.png"
    alt="TradeForge"
    className={className}
  />
);

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { isDark, setTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const isRtl = i18n.language === 'ar';

  // Helper to validate user data
  const isValidUser = (data) => {
    if (!data || typeof data !== 'object') return false;
    if ('type' in data && 'loc' in data && 'msg' in data) return false;
    return typeof data.email === 'string' || typeof data.name === 'string' || typeof data.user_id === 'string';
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { 
          withCredentials: true, 
          headers: getAuthHeaders(),
          timeout: 10000
        });
        if (isValidUser(response.data)) {
          setUser(response.data);
        } else {
          setUser(null);
        }
      } catch (error) {
        setUser(null);
      }
    };
    
    if (location.state?.user && isValidUser(location.state.user)) {
      setUser(location.state.user);
    } else {
      fetchUser();
    }
  }, [location.state]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success(t('logout') + ' ✓');
      navigate('/login');
    } catch (error) {
      toast.error(t('errors.something_went_wrong'));
    }
  };

  const navItems = [
    { name: t('nav.dashboard'), path: '/dashboard', icon: LayoutDashboard },
    { name: t('nav.signals'), path: '/signals', icon: TrendingUp },
    { name: t('nav.demo_trading'), path: '/demo-trading', icon: Play },
    { name: t('nav.trade_history'), path: '/trade-history', icon: History },
    { name: t('nav.notifications'), path: '/notifications', icon: Bell },
    { name: t('nav.pricing'), path: '/pricing', icon: CreditCard },
  ];

  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-[#060A10]' : 'bg-slate-50'}`} dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Settings Panel Modal */}
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
      
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Swiss High-Contrast Style */}
      <aside className={
        `fixed lg:static inset-y-0 ${isRtl ? 'right-0' : 'left-0'} z-50 w-64 ${
          isDark ? 'bg-[#060A10] border-[#1E293B]' : 'bg-white border-slate-200'
        } border-r transform transition-transform duration-200 ease-in-out ${
          sidebarOpen 
            ? 'translate-x-0' 
            : isRtl 
              ? 'translate-x-full lg:translate-x-0' 
              : '-translate-x-full lg:translate-x-0'
        }`
      }>
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Logo Header */}
          <div className={`flex items-center justify-between p-5 border-b ${isDark ? 'border-[#1E293B]' : 'border-slate-200'} flex-shrink-0`}>
            <div className="flex items-center gap-3">
              <TradeForgelogo className="w-10 h-10" />
              <span className="text-xl font-bold font-['Manrope'] tracking-tight bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                TradeForge
              </span>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className={`lg:hidden ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="p-3 space-y-1 flex-shrink-0">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={
                    `group flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-150 ${
                      isActive 
                        ? isDark 
                          ? 'bg-[#152A4A] text-cyan-400 border border-cyan-500/30' 
                          : 'bg-cyan-50 text-cyan-600 border border-cyan-200'
                        : isDark 
                          ? 'text-slate-400 hover:bg-[#0D131C] hover:text-white hover:border-[#1E293B] border border-transparent' 
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-transparent'
                    }`
                  }
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-cyan-400' : 'group-hover:text-cyan-400'}`} />
                  <span className="font-medium flex-1">{item.name}</span>
                  {isActive && <ChevronRight className={`w-4 h-4 ${isDark ? 'text-cyan-500/50' : 'text-cyan-400'}`} />}
                </Link>
              );
            })}

            {/* Admin link */}
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                onClick={() => setSidebarOpen(false)}
                className={
                  `group flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-150 ${
                    location.pathname === '/admin'
                      ? isDark 
                        ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30' 
                        : 'bg-purple-50 text-purple-600 border border-purple-200'
                      : isDark 
                        ? 'text-slate-400 hover:bg-[#0D131C] hover:text-white border border-transparent' 
                        : 'text-slate-600 hover:bg-slate-100 border border-transparent'
                  }`
                }
              >
                <Shield className={`w-5 h-5 ${location.pathname === '/admin' ? 'text-purple-400' : 'group-hover:text-purple-400'}`} />
                <span className="font-medium flex-1">{t('nav.admin')}</span>
                {location.pathname === '/admin' && <ChevronRight className="w-4 h-4 text-purple-500/50" />}
              </Link>
            )}
            
            {/* Divider */}
            <div className={`my-3 border-t ${isDark ? 'border-[#1E293B]' : 'border-slate-200'}`} />
            
            {/* Settings Button */}
            <button
              onClick={() => setSettingsOpen(true)}
              className={`w-full group flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-150 ${
                isDark 
                  ? 'text-slate-400 hover:bg-[#0D131C] hover:text-white' 
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
              data-testid="settings-btn"
            >
              <Settings className="w-5 h-5 group-hover:text-cyan-400" />
              <span className="font-medium flex-1">{t('settings.title')}</span>
            </button>
            
            {/* Quick Theme Toggle */}
            <button
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              className={`w-full group flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-150 ${
                isDark 
                  ? 'text-slate-400 hover:bg-[#0D131C] hover:text-white' 
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
              data-testid="theme-toggle-btn"
            >
              {isDark ? (
                <Sun className="w-5 h-5 group-hover:text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 group-hover:text-blue-400" />
              )}
              <span className="font-medium flex-1">{isDark ? t('settings.light_mode') : t('settings.dark_mode')}</span>
            </button>
          </nav>

          {/* User menu */}
          <div className={`p-3 border-t ${isDark ? 'border-[#1E293B]' : 'border-slate-200'} flex-shrink-0`}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={`w-full flex items-center gap-3 p-3 rounded-md transition-all duration-150 ${
                    isDark ? 'hover:bg-[#0D131C]' : 'hover:bg-slate-100'
                  }`} 
                  data-testid="user-menu-trigger"
                >
                  <Avatar className={`w-10 h-10 ring-2 ${isDark ? 'ring-[#1E293B]' : 'ring-slate-200'}`}>
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white font-semibold">
                      {user?.name?.[0] || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className={`flex-1 ${isRtl ? 'text-right' : 'text-left'}`}>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{user?.name || 'User'}</p>
                    <p className={`text-xs truncate ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{user?.email}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={`w-56 ${isDark ? 'bg-[#0D131C] border-[#1E293B]' : 'bg-white border-slate-200'}`}>
                <DropdownMenuItem 
                  onClick={() => navigate('/account')} 
                  data-testid="account-link" 
                  className={isDark ? 'focus:bg-[#152A4A]' : 'focus:bg-slate-100'}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  {t('nav.account')}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/support')} 
                  data-testid="support-link" 
                  className={isDark ? 'focus:bg-[#152A4A]' : 'focus:bg-slate-100'}
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Support
                </DropdownMenuItem>
                <DropdownMenuSeparator className={isDark ? 'bg-[#1E293B]' : 'bg-slate-200'} />
                <DropdownMenuItem 
                  onClick={handleLogout} 
                  data-testid="logout-btn" 
                  className="text-red-400 focus:text-red-300 focus:bg-red-500/10"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar - Glassmorphism */}
        <header className={`h-14 backdrop-blur-xl border-b flex items-center px-4 lg:px-8 sticky top-0 z-30 ${
          isDark ? 'bg-[#060A10]/80 border-[#1E293B]' : 'bg-white/80 border-slate-200'
        }`}>
          <button
            onClick={() => setSidebarOpen(true)}
            className={`lg:hidden mr-4 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2">
            <TradeForgelogo className="w-7 h-7" />
            <span className="font-bold font-['Manrope'] text-cyan-400">TradeForge</span>
          </div>
          
          <div className="flex-1" />
        </header>

        {/* Page content */}
        <main className={`flex-1 p-4 lg:p-6 ${isDark ? 'bg-[#060A10]' : 'bg-slate-50'}`}>
          {children}
        </main>
        
        {/* Contact Support Footer */}
        <footer className={`border-t px-4 lg:px-8 py-4 ${isDark ? 'bg-[#060A10] border-[#1E293B]' : 'bg-white border-slate-200'}`}>
          <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <div className="flex items-center gap-2">
              <TradeForgelogo className="w-5 h-5" />
              <span>© 2026 TradeForge. All rights reserved.</span>
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/support')}
                className={`flex items-center gap-2 transition-colors ${isDark ? 'hover:text-cyan-400' : 'hover:text-cyan-600'}`}
                data-testid="support-footer-link"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Support Center</span>
              </button>
              <button 
                onClick={() => {
                  const chatBtn = document.querySelector('[data-testid="chatbot-trigger"]');
                  if (chatBtn) chatBtn.click();
                }}
                className={`flex items-center gap-2 transition-colors ${isDark ? 'hover:text-cyan-400' : 'hover:text-cyan-600'}`}
                data-testid="contact-chat"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Live Chat</span>
              </button>
            </div>
          </div>
          <p className={`text-xs text-center mt-2 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
            Trading signals are for educational purposes only. Past performance does not guarantee future results.
          </p>
        </footer>
      </div>

      {/* AI Chatbot */}
      <AIChatbot />
    </div>
  );
}
